/**
 * ValuesAttentionCard — "Values needing attention" (Valuations Wave 1, F4).
 *
 * The engagement nudge (spec §2.3): staleness surfaced on the dashboard, not
 * buried in the History modal. Counts holdings past their revaluation due
 * date and lists them with a one-click "Update value" that opens the SAME
 * revalue modal the row actions use (`RevalueHoldingModal`).
 *
 * Count precedence: the backend rollup (`staleHoldingsCount` on
 * /v1/app/dashboard/trust-summary — Wave-1 B3) is authoritative when present;
 * an OLD backend without the field degrades to a client-side estimate derived
 * from each holding's `asAt` + the per-class cadence Paul locked in §8.1
 * (equity-listed monthly · property/fixed-income/cash quarterly ·
 * equity-private/alternatives/other annually). Nothing stale → renders null
 * (no empty-congratulations card).
 *
 * Unit note (§6): `HoldingRow.marketValueZar` is CENTS → `formatZarCents`.
 *
 * Discoverability fix (2026-08-01): this card already showed the last-valued
 * date (via `ValueProvenance`) and a one-click "Update value" — but nothing
 * let you see the dated history behind the staleness before deciding on a
 * new number. Each row now also offers "Valuation history", reusing the SAME
 * `ValuationHistoryModal` from holding-row-actions.tsx (no rebuild). Unlike
 * "Update value" (a write CTA, REQ-TIER-FE-001), this is a read action —
 * visible to every tier, beneficiary included.
 */

import { useState } from 'react';
import { RevalueHoldingModal, ValuationHistoryModal, linkBtn } from '../holding-row-actions';
import { ValueProvenance } from '../value-provenance';
import {
  assetClassLabel,
  formatZarCents,
  useCanActOnTrust,
  useDashboardHoldings,
  useDashboardTrustSummary,
  type HoldingAssetClass,
} from '../../lib/api';

/** Revaluation cadence in months per asset class (§8.1 — LOCKED). */
export const REVALUATION_CADENCE_MONTHS: Record<HoldingAssetClass, number> = {
  'equity-listed': 1,
  'equity-private': 12,
  'fixed-income': 3,
  cash: 3,
  property: 3,
  alternatives: 12,
  other: 12,
};

/** True when a holding valued `asAt` is past its per-class due date. PURE. */
export function isHoldingDue(
  asAt: string,
  assetClass: HoldingAssetClass,
  now: Date = new Date(),
): boolean {
  const valued = new Date(asAt);
  if (Number.isNaN(valued.getTime())) return false;
  const due = new Date(valued);
  due.setMonth(due.getMonth() + REVALUATION_CADENCE_MONTHS[assetClass]);
  return due.getTime() < now.getTime();
}

interface StaleRow {
  id: string;
  assetName: string;
  assetClass: HoldingAssetClass;
  /** CENTS. */
  marketValueZar: number;
  asAt: string;
}

export function ValuesAttentionCard({ trustId }: { trustId: string | undefined }) {
  const summaryQ = useDashboardTrustSummary(trustId);
  const holdingsQ = useDashboardHoldings(trustId);
  // REQ-TIER-FE-001: "Update value" is a WRITE CTA — a beneficiary-tier member
  // sees the staleness facts but not the click-to-revalue affordance.
  const canAct = useCanActOnTrust(trustId);
  const [revaluing, setRevaluing] = useState<StaleRow | null>(null);
  // Discoverability fix (2026-08-01): see the file header.
  const [viewingHistory, setViewingHistory] = useState<StaleRow | null>(null);

  if (!trustId || holdingsQ.isLoading || holdingsQ.isError) return null;

  const staleRows: StaleRow[] = [];
  for (const cls of holdingsQ.data?.byAssetClass ?? []) {
    for (const h of cls.holdings) {
      if (isHoldingDue(h.asAt, cls.assetClass)) {
        staleRows.push({
          id: h.id,
          assetName: h.assetName,
          assetClass: cls.assetClass,
          marketValueZar: h.marketValueZar,
          asAt: h.asAt,
        });
      }
    }
  }
  // Oldest first — the most overdue value is the one to chase.
  staleRows.sort((a, b) => a.asAt.localeCompare(b.asAt));

  // Backend rollup wins when present (it knows per-holding nextDueAt); the
  // derived list is the fallback + supplies the row detail either way.
  const count = summaryQ.data?.staleHoldingsCount ?? staleRows.length;
  if (count <= 0) return null;

  return (
    <div className="row">
    <div className="card" data-testid="values-attention-card">
      <div className="card-head">
        <div>
          <div className="eyebrow" style={{ color: 'var(--accent-700)' }}>Valuations</div>
          <h2 className="card-title">Values needing attention</h2>
        </div>
        <span className="card-meta">
          {count} holding{count === 1 ? '' : 's'} due for revaluation
        </span>
      </div>

      {staleRows.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13, padding: '4px 0', lineHeight: 1.6 }}>
          {count} holding{count === 1 ? ' is' : 's are'} due for revaluation. Open the
          Portfolios tab to record updated values.
        </p>
      ) : (
        <div>
          {staleRows.map((r, i) => (
            <div
              key={r.id}
              data-testid={`stale-holding-${r.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '11px 0',
                borderBottom: i === staleRows.length - 1 ? 'none' : '1px solid var(--hairline)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.assetName}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 1 }}>
                  {assetClassLabel(r.assetClass)}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 13.5, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>
                  {formatZarCents(r.marketValueZar)}
                </div>
                <div style={{ marginTop: 3 }}>
                  <ValueProvenance asAt={r.asAt} stale compact />
                </div>
              </div>
              {/* Discoverability fix (2026-08-01): a read action, so — unlike
                  "Update value" below — it stays visible to every tier. */}
              {/* Surface-specific testid — see the note in holdings-table.tsx.
                  This card and the holdings table both render on the dashboard,
                  so a shared testid would match twice on one page. */}
              <button
                type="button"
                data-testid={`history-holding-attention-${r.id}`}
                onClick={() => setViewingHistory(r)}
                style={{ ...linkBtn, color: 'var(--ink)', flexShrink: 0 }}
              >
                Valuation history
              </button>
              {canAct ? (
                <button
                  type="button"
                  className="btn-secondary"
                  data-testid={`update-value-${r.id}`}
                  onClick={() => setRevaluing(r)}
                  style={{ fontSize: 12.5, padding: '6px 12px', flexShrink: 0 }}
                >
                  Update value
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {revaluing && (
        <RevalueHoldingModal
          trustId={trustId}
          holdingId={revaluing.id}
          name={revaluing.assetName}
          marketValueZar={revaluing.marketValueZar}
          noun="holding"
          onClose={() => setRevaluing(null)}
        />
      )}

      {viewingHistory && (
        <ValuationHistoryModal
          trustId={trustId}
          holdingId={viewingHistory.id}
          name={viewingHistory.assetName}
          onClose={() => setViewingHistory(null)}
        />
      )}
    </div>
    </div>
  );
}
